import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useFetch } from './api';
import SettingsHeader from './SettingsHeader';

function Notifications() {
  const { user } = useUser();
  const trackstar = useFetch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [preferences, setPreferences] = useState({});

  const userEmail = user?.primaryEmailAddress?.emailAddress;

  useEffect(() => {
    trackstar('internal/notification-types')
      .then((response) => {
        setNotificationTypes(response.notification_types || []);
      })
      .catch((error) => {
        console.error('Failed to fetch notification types:', error);
      });
  }, []);

  useEffect(() => {
    if (!userEmail) return;

    trackstar(`internal/user-notification-settings?email=${encodeURIComponent(userEmail)}`)
      .then((response) => {
        setPreferences(response.subscriptions || {});
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [userEmail]);

  const handleToggle = async (notificationType) => {
    const newPreferences = {
      ...preferences,
      [notificationType]: !preferences[notificationType],
    };
    setPreferences(newPreferences);
    setSaving(true);

    try {
      await trackstar('internal/user-notification-settings', 'PUT', {
        email: userEmail,
        subscriptions: newPreferences,
      });
    } catch (error) {
      setPreferences(preferences);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !userEmail) {
    return (
      <div className="flex flex-col gap-5">
        <SettingsHeader title="Notifications" subtitle="Manage your notification preferences" />
        <p className="text-sm text-surface-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <SettingsHeader title="Notifications" subtitle="Manage your notification preferences" />

      <div className="card p-6 flex flex-col gap-6">
        <div className="border-b border-surface-100 pb-6">
          <h3 className="text-sm font-semibold text-surface-800 mb-2">Email Address</h3>
          <p className="text-sm text-surface-500">
            Notifications will be sent to:
            {' '}
            <strong className="text-surface-700">{userEmail}</strong>
          </p>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-surface-800 mb-2">Event Types</h3>

          {notificationTypes.map((notificationType) => (
            <div key={notificationType.key} className="flex items-center justify-between py-3">
              <div className="flex flex-col">
                <span className="font-medium text-surface-800">{notificationType.display_name}</span>
                <span className="text-sm text-surface-500">
                  {notificationType.description}
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleToggle(notificationType.key)}
                disabled={saving}
                aria-label={`Toggle ${notificationType.display_name} notifications`}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${preferences[notificationType.key] === true ? 'bg-trackstarAllDark' : 'bg-gray-300'
                } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${preferences[notificationType.key] === true ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Notifications;
